<!-- Main Container -->
<div class="min-h-screen bg-slate-50 text-slate-900 pb-20">
  
  <!-- Header Section -->
  <div class="bg-white border-b border-slate-200 sticky top-16 z-10 shadow-sm">
    <div class="container mx-auto px-4 py-4">
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div class="flex items-center gap-2 text-sm text-slate-500 mb-1">
            <a routerLink="/" class="hover:text-cyan-600 transition-colors">Home</a>
            <i class='bx bx-chevron-right'></i>
            <span class="text-slate-900 font-medium">Load Balancers</span>
          </div>
          <h1 class="text-2xl font-bold text-slate-900">Load Balancers Management</h1>
        </div>
        <button (click)="openCreateModal()" class="flex items-center gap-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white px-5 py-2.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 font-medium">
          <i class='bx bx-plus'></i>
          Create Load Balancer
        </button>
      </div>
    </div>
  </div>

  <div class="container mx-auto px-4 py-8">
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 h-[calc(100vh-220px)] min-h-[600px]">
      
      <!-- Left Column: List View -->
      <div class="lg:col-span-5 flex flex-col bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <!-- Controls -->
        <div class="p-4 border-b border-slate-100 bg-slate-50/50 space-y-3">
          <div class="relative">
            <i class='bx bx-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400'></i>
            <input 
              type="text" 
              placeholder="Search load balancers..." 
              class="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all text-sm"
              (input)="onSearch($event)"
            >
          </div>
          <div class="flex gap-2 overflow-x-auto pb-1 custom-scrollbar">
            <button 
              *ngFor="let status of ['All', 'Active', 'Inactive', 'Provisioning', 'Error']"
              (click)="onStatusFilterChange(status)"
              [class.bg-cyan-100]="statusFilter === status"
              [class.text-cyan-800]="statusFilter === status"
              [class.border-cyan-200]="statusFilter === status"
              [class.bg-white]="statusFilter !== status"
              [class.text-slate-600]="statusFilter !== status"
              class="px-3 py-1.5 rounded-full text-xs font-medium border border-slate-200 hover:border-cyan-300 transition-colors whitespace-nowrap"
            >
              {{ status }}
            </button>
          </div>
        </div>

        <!-- List Content -->
        <div class="flex-1 overflow-y-auto custom-scrollbar">
          <div *ngIf="filteredLoadBalancers.length === 0" class="flex flex-col items-center justify-center h-full p-8 text-center text-slate-500">
            <i class='bx bx-search text-4xl mb-2 text-slate-300'></i>
            <p>No load balancers found</p>
          </div>

          <div *ngFor="let lb of filteredLoadBalancers" 
               (click)="selectLoadBalancer(lb)"
               [class.bg-cyan-50]="selectedLoadBalancer?.id === lb.id"
               [class.border-l-4]="selectedLoadBalancer?.id === lb.id"
               [class.border-l-cyan-500]="selectedLoadBalancer?.id === lb.id"
               [class.border-l-transparent]="selectedLoadBalancer?.id !== lb.id"
               class="p-4 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-all group"
          >
            <div class="flex justify-between items-start mb-1">
              <h3 class="font-semibold text-slate-900 group-hover:text-cyan-700 transition-colors">{{ lb.name }}</h3>
              <span [class]="'text-xs px-2 py-0.5 rounded-full border ' + getStatusColor(lb.status)">
                {{ lb.status }}
              </span>
            </div>
            <div class="flex items-center gap-4 text-xs text-slate-500 mb-2">
              <span class="flex items-center gap-1"><i class='bx bx-map'></i> {{ lb.region }}</span>
              <span class="flex items-center gap-1"><i class='bx bx-server'></i> {{ lb.backendServers }} nodes</span>
            </div>
            <div class="text-xs font-mono text-slate-400 truncate">
              {{ lb.ipAddress }}
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="p-3 border-t border-slate-200 bg-slate-50 flex justify-between items-center text-sm">
          <span class="text-slate-500">Page {{ currentPage }} of {{ totalPages }}</span>
          <div class="flex gap-1">
            <button (click)="prevPage()" [disabled]="currentPage === 1" class="p-1.5 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed text-slate-600">
              <i class='bx bx-chevron-left text-lg'></i>
            </button>
            <button (click)="nextPage()" [disabled]="currentPage === totalPages" class="p-1.5 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed text-slate-600">
              <i class='bx bx-chevron-right text-lg'></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Right Column: Detailed View -->
      <div class="lg:col-span-7 flex flex-col bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden relative">
        <div *ngIf="!selectedLoadBalancer" class="absolute inset-0 flex flex-col items-center justify-center text-slate-400 bg-slate-50/50">
          <i class='bx bx-layer text-6xl mb-4 text-slate-300'></i>
          <p class="text-lg font-medium">Select a load balancer to view details</p>
        </div>

        <div *ngIf="selectedLoadBalancer" class="flex flex-col h-full">
          <!-- Detail Header -->
          <div class="p-6 border-b border-slate-100 bg-white">
            <div class="flex justify-between items-start mb-4">
              <div>
                <h2 class="text-2xl font-bold text-slate-900 mb-1">{{ selectedLoadBalancer.name }}</h2>
                <div class="flex items-center gap-3 text-sm text-slate-500">
                  <span class="font-mono bg-slate-100 px-2 py-0.5 rounded text-slate-600">{{ selectedLoadBalancer.id }}</span>
                  <span class="flex items-center gap-1"><i class='bx bx-time-five'></i> Uptime: {{ selectedLoadBalancer.uptime }}</span>
                </div>
              </div>
              <div class="flex gap-2">
                <button class="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all" title="Edit Configuration">
                  <i class='bx bx-edit-alt text-xl'></i>
                </button>
                <button class="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Delete Load Balancer">
                  <i class='bx bx-trash text-xl'></i>
                </button>
              </div>
            </div>
            
            <!-- Tabs -->
            <div class="flex gap-6 border-b border-slate-100">
              <button 
                *ngFor="let tab of ['overview', 'metrics', 'logs']"
                (click)="activeTab = tab"
                class="pb-3 text-sm font-medium capitalize transition-all relative"
                [class.text-cyan-600]="activeTab === tab"
                [class.text-slate-500]="activeTab !== tab"
              >
                {{ tab }}
                <span *ngIf="activeTab === tab" class="absolute bottom-0 left-0 w-full h-0.5 bg-cyan-600 rounded-t-full"></span>
              </button>
            </div>
          </div>

          <!-- Detail Content -->
          <div class="flex-1 overflow-y-auto p-6 bg-slate-50/30 custom-scrollbar">
            
            <!-- Overview Tab -->
            <div *ngIf="activeTab === 'overview'" class="space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <!-- Network Config -->
                <div class="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <h3 class="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <i class='bx bx-network-chart text-cyan-500'></i> Network Configuration
                  </h3>
                  <div class="space-y-3 text-sm">
                    <div class="flex justify-between py-2 border-b border-slate-50">
                      <span class="text-slate-500">IP Address</span>
                      <span class="font-mono text-slate-700 select-all">{{ selectedLoadBalancer.ipAddress }}</span>
                    </div>
                    <div class="flex justify-between py-2 border-b border-slate-50">
                      <span class="text-slate-500">Type</span>
                      <span class="text-slate-700">{{ selectedLoadBalancer.type }}</span>
                    </div>
                    <div class="flex justify-between py-2 border-b border-slate-50">
                      <span class="text-slate-500">Ports</span>
                      <div class="flex gap-1">
                        <span *ngFor="let port of selectedLoadBalancer.ports" class="bg-slate-100 px-2 py-0.5 rounded text-xs font-mono">{{ port }}</span>
                      </div>
                    </div>
                    <div class="flex justify-between py-2">
                      <span class="text-slate-500">Region</span>
                      <span class="text-slate-700 flex items-center gap-1"><img src="assets/flags/us.svg" class="w-4 h-3 object-cover rounded-sm hidden"> {{ selectedLoadBalancer.region }}</span>
                    </div>
                  </div>
                </div>

                <!-- Backend Status -->
                <div class="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <h3 class="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <i class='bx bx-server text-cyan-500'></i> Backend Status
                  </h3>
                  <div class="flex flex-col items-center justify-center py-4">
                    <div class="w-32 h-32 rounded-full border-8 border-slate-100 border-t-emerald-500 flex flex-col items-center justify-center mb-4 relative">
                      <span class="text-3xl font-bold text-slate-900">{{ selectedLoadBalancer.backendServers }}</span>
                      <span class="text-xs text-slate-500">Nodes</span>
                    </div>
                    <p class="text-sm text-emerald-600 font-medium flex items-center gap-1">
                      <i class='bx bx-check-circle'></i> All systems operational
                    </p>
                  </div>
                </div>
              </div>

              <!-- Recent Activity Snippet -->
              <div class="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                 <h3 class="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Latest Activity</h3>
                 <div class="space-y-3">
                   <div *ngFor="let log of selectedLoadBalancer.logs.slice(0, 2)" class="flex gap-3 text-sm items-start">
                     <span [class]="'mt-1 w-2 h-2 rounded-full flex-shrink-0 ' + (log.level === 'Error' ? 'bg-red-500' : (log.level === 'Warning' ? 'bg-amber-500' : 'bg-emerald-500'))"></span>
                     <div>
                       <p class="text-slate-700">{{ log.message }}</p>
                       <p class="text-xs text-slate-400">{{ log.timestamp | date:'medium' }}</p>
                     </div>
                   </div>
                 </div>
              </div>
            </div>

            <!-- Metrics Tab -->
            <div *ngIf="activeTab === 'metrics'" class="space-y-6">
              <div class="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <h3 class="text-sm font-bold text-slate-900 mb-4">Requests Per Second</h3>
                <div class="h-32 flex items-end justify-between gap-1">
                  <div *ngFor="let val of selectedLoadBalancer.metrics.requestsPerSecond" 
                       [style.height.%]="(val / 1500) * 100" 
                       class="w-full bg-cyan-100 hover:bg-cyan-200 rounded-t transition-all relative group">
                    <div class="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      {{ val }}
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <h3 class="text-sm font-bold text-slate-900 mb-4">Latency (ms)</h3>
                <div class="h-32 flex items-end justify-between gap-1">
                  <div *ngFor="let val of selectedLoadBalancer.metrics.latency" 
                       [style.height.%]="(val / 100) * 100" 
                       class="w-full bg-indigo-100 hover:bg-indigo-200 rounded-t transition-all relative group">
                     <div class="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      {{ val }}ms
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Logs Tab -->
            <div *ngIf="activeTab === 'logs'" class="space-y-4">
              <div *ngFor="let log of selectedLoadBalancer.logs" class="bg-white p-4 rounded-lg border border-slate-200 shadow-sm flex gap-4">
                <div [class]="'w-1 self-stretch rounded-full ' + (log.level === 'Error' ? 'bg-red-500' : (log.level === 'Warning' ? 'bg-amber-500' : 'bg-emerald-500'))"></div>
                <div class="flex-1">
                  <div class="flex justify-between items-start mb-1">
                    <span [class]="'text-xs font-bold px-2 py-0.5 rounded ' + (log.level === 'Error' ? 'bg-red-100 text-red-800' : (log.level === 'Warning' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'))">
                      {{ log.level }}
                    </span>
                    <span class="text-xs font-mono text-slate-400">{{ log.timestamp | date:'medium' }}</span>
                  </div>
                  <p class="text-sm text-slate-700">{{ log.message }}</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
      
    </div>
  </div>
</div>

<!-- Create Modal -->
<div *ngIf="isCreateModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm transition-opacity">
  <div class="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-fade-in-up">
    <div class="p-6 border-b border-slate-100 flex justify-between items-center">
      <h3 class="text-xl font-bold text-slate-900">Create Load Balancer</h3>
      <button (click)="closeCreateModal()" class="text-slate-400 hover:text-slate-600 transition-colors">
        <i class='bx bx-x text-2xl'></i>
      </button>
    </div>
    
    <div class="p-6 space-y-4">
      <div>
        <label class="block text-sm font-medium text-slate-700 mb-1">Name</label>
        <input [(ngModel)]="newLbName" type="text" class="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 outline-none transition-all" placeholder="e.g. web-production-lb">
      </div>
      
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-1">Type</label>
          <select [(ngModel)]="newLbType" class="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 outline-none transition-all bg-white">
            <option value="Application">Application (HTTP/S)</option>
            <option value="Network">Network (TCP/UDP)</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-1">Region</label>
          <select [(ngModel)]="newLbRegion" class="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 outline-none transition-all bg-white">
            <option value="us-east-1">US East (N. Virginia)</option>
            <option value="us-west-2">US West (Oregon)</option>
            <option value="eu-central-1">EU (Frankfurt)</option>
          </select>
        </div>
      </div>

      <div class="bg-slate-50 p-4 rounded-lg border border-slate-200 text-sm text-slate-600">
        <p class="flex items-center gap-2 mb-2 font-medium text-slate-900"><i class='bx bx-info-circle text-cyan-500'></i> Summary</p>
        <ul class="space-y-1 pl-6 list-disc marker:text-slate-400">
          <li>Standard SLA (99.95%)</li>
          <li>Auto-scaling enabled</li>
          <li>DDoS protection included</li>
        </ul>
      </div>
    </div>

    <div class="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
      <button (click)="closeCreateModal()" class="px-5 py-2 text-slate-600 font-medium hover:bg-slate-200 rounded-lg transition-colors">Cancel</button>
      <button (click)="createLoadBalancer()" class="px-5 py-2 bg-cyan-600 text-white font-medium rounded-lg hover:bg-cyan-700 shadow-md hover:shadow-lg transition-all">Create Resource</button>
    </div>
  </div>
</div>
